import { AfterViewInit, Component, EventEmitter, HostListener, Injector, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ChecklistTemplate, Checklist } from 'src/app/components/checklists/checklists';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { TransactionLogService } from 'src/app/services/logs/transaction-log.service';
import { TransactionLog } from 'src/app/services/logs/transaction-logs';
import { PVData } from '../cl-image-upload-table/cl-image-upload-table-data/cl-image-upload-table-data.component';
import { ClImageUploadTableComponent } from '../cl-image-upload-table/cl-image-upload-table.component';
import { TableColumn, TableData } from '../cl-image-upload.component';
import { RadTablePanel } from '../image-table-popup/image-table-popup.component';
import { BaseComponent } from 'src/app/common/base/base.component';

@Component({
  selector: 'cl-image-upload-content',
  templateUrl: './cl-image-upload-content.component.html',
  styleUrls: ['./cl-image-upload-content.component.scss']
})
export class ClImageUploadContentComponent extends BaseComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() enableColumnsBuilder!: boolean;
  @Input() enableColumnsChecklist!: boolean;
  @Input() checklistTemplate?: ChecklistTemplate;
  @Input() selectedPanel: number = 0;
  @Input() configuration?: RadTableConfiguration;
  @Input() editor?: boolean;
  @Input() builder?: boolean;
  @Input() roleIds?: number[];
  @Input() disabled?: boolean;
  @Input() checklist?: Checklist;
  @Input() taskID?: number;

  @Output() changed = new EventEmitter<RadTableConfiguration>();

  public columnsBuilder!: TableColumn[];
  public columnsChecklist!: TableColumn[];
  public columns!: string[];

  public row: any;
  public colorize!: boolean;

  tableClass = 'table-container';

  context!: CanvasRenderingContext2D;

  @ViewChild('mycanvas') mycanvas: any;
  @ViewChild('containerCanvas') containerCanvas: any;
  @ViewChild(ClImageUploadTableComponent) dataTable?: ClImageUploadTableComponent;

  imagesList: ListImage[] = [];
  images: string[] = [];
  colWidths!: number[];
  ImageSpotId = 0;
  rowId = 0;
  imageId!: number;
  action!: Action;

  showImageOptions!: boolean;
  panel = RadTablePanel;

  logs?: TransactionLog[];

  originalConfiguration?: RadTableConfiguration;
  compareConfiguration?: RadTableConfiguration;

  private resizeTimer: any;

  constructor(
    protected override injector: Injector,
    private log: TransactionLogService
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.innerWidth = window.innerWidth;

    if ((this.editor || !this.builder) && this.configuration) this.loadCanvas();
    if (changes['selectedPanel']) {
      if (changes['selectedPanel'].currentValue == RadTablePanel.History)
        this.originalConfiguration = this.utils.cloneDeep(this.configuration);
      if ((changes['selectedPanel'].currentValue == RadTablePanel.Image || changes['selectedPanel'].currentValue == RadTablePanel.Table) && this.originalConfiguration)
        this.configuration = this.utils.cloneDeep(this.originalConfiguration);
    }
    this.tableClass = this.selectedPanel == RadTablePanel.History ? 'table-container-short' : 'table-container';
  }

  ngAfterViewInit() {
    if (this.configuration) {
      this.loadCanvas();
    }
  }

  loadCanvas(configuration?: RadTableConfiguration) {
    const canvas = this.mycanvas?.nativeElement;
    if (!canvas) {
      setTimeout(() => {
        this.loadCanvas(configuration);
      }, 500);
    }
    else
      setTimeout(() => {
        this.loadImages();
      }, 100);
  }


  async loadImages(): Promise<void> {
    this.innerWidth = window.innerWidth;

    if (!this.configuration) {
      return;
    }

    this.imagesList = this.configuration.images ?? [];
    this.images = this.configuration.images?.map((x: any) => x.image) ?? [];

    const spots = this.configuration.spots as Spot[] | undefined;
    const canvas = this.mycanvas?.nativeElement as HTMLCanvasElement | undefined;

    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    if (!this.images.length) {
      canvas.width = 0;
      canvas.height = 0;
      context.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    context.globalCompositeOperation = 'destination-over';
    context.clearRect(0, 0, canvas.width, canvas.height);

    const loadedImages = await this.imagesArray();

    if (!loadedImages.length) {
      canvas.width = 0;
      canvas.height = 0;
      return;
    }

    const totalWidth = loadedImages.reduce((sum, image) => sum + image.width, 0);
    const maxHeight = Math.max(...loadedImages.map(image => image.height));

    canvas.width = totalWidth;
    canvas.height = maxHeight;

    loadedImages.forEach((image, index) => {
      context.drawImage(image, this.calcStart(loadedImages, index), 0);
    });

    if (spots?.length) {
      this.loadSpots(spots);
    }
  }


  async imagesArray(): Promise<HTMLImageElement[]> {
    const imagePromises = this.images.map(img => this.imagePush(img));
    return Promise.all(imagePromises);
  }

  async imagePush(i: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = i;

      img.onload = () => resolve(img);
      img.onerror = reject;
    });
  }


  calcStart(images: any[], idx: number) {
    let width = 0;
    for (let i = 0; i < idx; i++) {
      width += images[i].width;
    }
    return width;
  }

  imageClicked(event: MouseEvent): void {
    if (!this.configuration) {
      return;
    }

    const spots = this.configuration.spots as Spot[];
    const dataset = this.configuration.data ?? [];
    const listImages: ListImage[] = this.configuration.images ?? [];
    const canvas = this.mycanvas?.nativeElement as HTMLCanvasElement | undefined;

    if (!canvas || !listImages.length) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const { width: canvasWidth, height: canvasHeight } = this.getCanvasDimensions(listImages);

    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    const scaledX = canvasWidth * (clickX / rect.width);
    const scaledY = canvasHeight * (clickY / rect.height);

    const imageId = this.calcImageid(scaledX, listImages);
    const [percentage] = this.calcPercentages(listImages, scaledX, scaledY, canvasHeight);

    if (!this.row) {
      this.showImageDialog(imageId, spots);
      return;
    }

    this.addSpotToSelectedRow(spots, dataset, imageId, percentage.percX, percentage.percY);
  }

  private getCanvasDimensions(listImages: ListImage[]): { width: number; height: number } {
    return listImages.reduce(
      (result, item) => {
        result.width += item.imageWidth;
        result.height = Math.max(result.height, item.imageHeight);
        return result;
      },
      { width: 0, height: 0 }
    );
  }

  private addSpotToSelectedRow(
    spots: Spot[],
    dataset: any[],
    imageId: number,
    percX: number,
    percY: number
  ): void {
    if (!this.row) {
      return;
    }

    const spotId = this.row.rowId;

    spots.push({
      id: this.row.id,
      posx: 0,
      posy: 0,
      spotText: spotId,
      imageid: imageId,
      percX,
      percY,
      origin: this.editor ? SpotOrigin.BUILDER : SpotOrigin.CHECKLIST,
    });

    const row = dataset?.find((x: any) => x.rowId === spotId);
    if (row) {
      row.spotid = spotId;
      row.imageId = imageId;
    }

    this.action = Action.None;
    this.row = null;
    this.loadImages();

    this.emitChangedConfiguration(this.configuration!);
    this.selectedPanel = RadTablePanel.Table;
    if (this.dataTable) {
      this.dataTable.addingSpot = false;
      this.dataTable.renderTable();
    }

  }

  emitChangedConfiguration(configuration: RadTableConfiguration) {
    this.loadCanvas(configuration);
    this.changed.emit(configuration);
  }

  showImageDialog(imageId?: number, spots?: Spot[]) {
    if (this.checkRoles()) {
      this.showImageOptions = true;
      this.action = Action.ImageOptions;
      if (imageId) {
        const image = this.imagesList.find(i => i.id == imageId);
        if (image) {
          image.canBeDeleted = !spots?.some(s => s.imageid == image.id && s.origin == SpotOrigin.BUILDER);
          this.imageId = image.id;
        }
      }
    }
  }

  scrollParentToChild(child: any) {
    const parent = document.getElementById('scrollableContainer');
    if (parent) {
      // Where is the parent on page
      const parentRect = parent.getBoundingClientRect();
      // What can you see?
      const parentViewableArea = {
        height: parent.clientHeight,
        width: parent.clientWidth
      };

      // Where is the child
      const childRect = child.getBoundingClientRect();
      // Is the child viewable?
      const isViewable = (childRect.top >= (parentRect.top ?? 0)) && (childRect.bottom <= (parentRect.top ?? 0) + (parentViewableArea.height ?? 0));

      // if you can't see the child try to scroll parent
      if (!isViewable) {
        // Should we scroll using top or bottom? Find the smaller ABS adjustment
        const scrollTop = childRect.top - (parentRect.top ?? 0);
        parent.scrollTop += childRect.top * 1.2;
      }
    }
  }

  calcImageid(dotX: number, listImages: ListImage[]): number {
    let imgWidth1 = 0;
    let imgWidth2 = 0;
    let imgId = 0;

    listImages.map(item => {
      imgWidth2 = imgWidth2 + item.imageWidth;
      if (dotX > imgWidth1 && dotX <= imgWidth2) {
        imgId = item.id;
      }
      imgWidth1 = imgWidth2;
    });
    return imgId;
  }

  calcImagePercents(listImages: ListImage[], totalImagesW: number, totalImagesH: number): any[] {
    const imagesPercent: any[] = [{ id: 0, percentW: 0, percentH: 0 }];
    listImages.map(img => {
      const pW = img.imageWidth / totalImagesW;
      const pH = img.imageHeight / totalImagesH;
      imagesPercent.push({ id: img.id, percentW: pW, percentH: pH });
    });
    return imagesPercent;
  }

  calcPercentages(listImages: ListImage[], dotX: number, dotY: number, totalImagesH: number): any[] {
    const percentagesSpot = [{ percX: 0, percY: 0 }];
    let imgWidth1 = 0;
    let imgWidth2 = 0;
    let imgHeight = 0;

    listImages.map(item => {
      imgWidth2 = imgWidth2 + item.imageWidth;
      imgHeight = item.imageHeight;
      if (dotX > imgWidth1 && dotX <= imgWidth2) {
        const pointxImageCalculated = (dotX - imgWidth1);
        const pointxImagePercent = (pointxImageCalculated / item.imageWidth);
        const pointyImagePercent = dotY / (totalImagesH * (imgHeight / totalImagesH));
        percentagesSpot[0].percX = pointxImagePercent;
        percentagesSpot[0].percY = pointyImagePercent;
      }
      imgWidth1 = imgWidth2;
    });

    return percentagesSpot;
  }

  calcCoordinates(listImages: ListImage[], imagesPercents: any[], percentX: number, percentY: number, spotImageId: number, totalImagesW: number, totalImagesH: number) {
    const spotCoordinates: any[] = [{ x: 0, y: 0 }];
    let widthX = 0;
    listImages.map(img => {
      const percentImage: any[] = imagesPercents.filter(x => x.id == img.id);
      const widthImage = (totalImagesW * percentImage[0].percentW);
      if (img.id < spotImageId) {
        widthX = widthX + widthImage;
      }
      else if (img.id == spotImageId) {
        const widthCalculated = widthImage * percentX;
        spotCoordinates[0].x = widthX + widthCalculated;
        spotCoordinates[0].y = (totalImagesH * (percentY * percentImage[0].percentH));
      }
    });

    return spotCoordinates;
  }

  loadSpots(spots: Spot[]) {
    let hcanvas = 0;
    let wcanvas = 0;
    const listImages: ListImage[] = this.imagesList;
    listImages.map(item => {
      wcanvas = wcanvas + item.imageWidth;
      if (item.imageHeight > hcanvas) {
        hcanvas = item.imageHeight;
      }
    });

    const imagePercents = this.calcImagePercents(listImages, wcanvas, hcanvas);

    spots.map(s => {
      if (s.id != 0) {
        const coordinates = this.calcCoordinates(listImages, imagePercents, s.percX, s.percY, s.imageid, wcanvas, hcanvas);
        const x = Math.round(coordinates[0].x);
        const y = Math.round(coordinates[0].y);
        this.drawSpot(+s.spotText, x, y, s.origin ? s.origin : SpotOrigin.BUILDER);
      }
    });
  }

  drawSpot(spotNewId: number, x: number, y: number, origin: SpotOrigin) {
    const ctx = this.mycanvas.nativeElement.getContext('2d');
    const canvasWidth = this.mycanvas.nativeElement.width;
    const ratio = canvasWidth / this.innerWidth;

    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    ctx.shadowBlur = 2;
    ctx.fillStyle = origin == SpotOrigin.BUILDER ? 'rgba(240, 0, 0, 0.7)' : 'rgba(0, 60, 230, 0.7)';
    ctx.strokeStyle = 'rgb(0, 0, 0, .5)';

    const radius = 19 * ratio;
    const font = 17 * ratio;

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2, true);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = font + 'px Verdana';
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowBlur = 0;
    ctx.fillText(spotNewId.toString(), x, y);
  }

  nextId(): number {
    let id = 0;
    const uniqueIds = [...new Set(this.configuration?.data?.map((obj: any) => obj.id))] as number[];
    let arrayIds: number[] = [];
    uniqueIds.map(n => {
      arrayIds.push(n);
    });
    arrayIds = arrayIds.sort((n1, n2) => n1 - n2);
    id = (Number(Math.max.apply(null, arrayIds)) + 1);
    return id;
  }

  imageChanged(e: any) {
    const images = e as ListImage[];
    if (!this.configuration) {
      this.configuration = { images, spots: [], data: [], columnsBuilder: [], columnsChecklist: [] };
      this.emitChangedConfiguration(this.configuration);
      return;
    }
    let spots = this.configuration?.spots as Spot[];
    let data = this.configuration?.data;
    const spotsToRemove = spots.filter(s => !images.map(i => i.id).includes(s.imageid));
    spotsToRemove.map(x => {
      var spot = data?.find((d: any) => d.spotid == x.spotText);
      if (spot)
        spot.spotid = '';
    });

    spots = spots.filter(s => images.map(i => i.id).includes(s.imageid));
    this.configuration.images = images;
    this.configuration.spots = spots;
    this.emitChangedConfiguration(this.configuration)
  }

  tableChange(configuration: RadTableConfiguration) {
    this.configuration = configuration;
    this.emitChangedConfiguration(configuration);
  }

  addRow(id: number) {
    if (id) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: '400px',
        disableClose: true,
        data: {
          message: this.getMessage('Rad_Survey_Image_Component_Add_Row').description,
          icon: 'stop'
        }
      });
      confirm.afterClosed().subscribe(data => {
        if (data) {
          const dataset = this.configuration?.data;
          const dsRows = dataset?.filter((x: any) => x.id == id);
          const firstRow = dsRows?.[0];
          const channels = this.utils.JSONparse(firstRow?.data) as PVData[];
          channels?.map(c => {
            c.date = null;
            c.error = false;
            c.updating = false;
            c.user = null;
            c.value = null;
          });
          const lastRow = dsRows?.[dsRows.length - 1];
          const lastRowId = lastRow?.rowId.toString().split('.');
          const rowId = lastRowId?.[0] + '.' + (lastRowId?.[1] ? (+lastRowId?.[1] + 1).toString() : 1);
          dataset?.push({ id, values: '', rowId, spotid: 0, imageid: 0, idSpan: 1, data: this.utils.JSONstringify(channels) });

          if (this.configuration) {
            this.dataTable?.renderTable();
            this.emitChangedConfiguration(this.configuration);
          }
        }
      });
    }
  }

  deleteRow(element: any) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        message: this.getMessage('Rad_Survey_Image_Component_Delete_Row').description,
        icon: 'stop'
      }
    });
    confirm.afterClosed().subscribe(data => {
      if (data) {
        const dataset = this.configuration?.data as TableData[];
        const index = dataset.findIndex(x => x.rowId == element.rowId);
        dataset.splice(index, 1);
        this.configuration = this.reorderRows(this.configuration);
        this.dataTable?.renderTable();
        this.emitChangedConfiguration(this.configuration!);
      }
    });
  }

  duplicateRow(r: any) {
    const dataset = this.configuration?.data as TableData[];
    const rows = dataset.filter(x => x.id == r.id);
    const newId = this.nextId();
    rows.map(row => {
      const newRow = this.utils.cloneDeep(row);
      newRow.id = newId;
      newRow.rowId = newId + '.' + newRow.rowId.split('.')[1];
      newRow.spotid = 0;

      dataset.push(newRow);
    });

    if (this.configuration) {
      this.configuration.data = dataset;
      this.dataTable?.renderTable();
      this.emitChangedConfiguration(this.configuration);
    }
  }

  reorderRows(configuration: any) {
    const rowIds: string[] = [];
    const dataset = configuration.data;
    const spots = configuration.spots;
    const newDataset: any[] = [];
    dataset.map((s: any) => {
      if (!rowIds.includes(s.id)) {
        rowIds.push(s.id);
      }
    });
    rowIds.map(r => {
      const rrows = dataset.filter((s: any) => s.id == r);
      const srows: SpotsOrder[] = [];
      let o = 1;
      rrows.sort((a: any, b: any) => a.rowId.toString().split('.')[1] - b.rowId.toString().split('.')[1]).map((s: any) => {
        srows.push({ id: s.id, rowId: s.rowId, newRowId: s.id + '.' + o.toString() } as SpotsOrder);
        o++;
      });
      srows.map(n => {
        const newRow = this.utils.cloneDeep(dataset.find((d: any) => d.rowId == n.rowId));
        newRow.rowId = n.newRowId;
        newDataset.push(newRow);
        const spot = spots.find((s: any) => s.spotText == n.rowId);
        if (spot) {
          spot.spotText = n.newRowId;
        }
      });
    });
    configuration.data = newDataset;
    return configuration;
  }

  deleteSpot(element: any) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        message: this.getMessage('Rad_Survey_Image_Component_Delete_Spot').description,
        icon: 'stop'
      }
    });

    confirm.afterClosed().subscribe(data => {
      if (data && this.configuration) {
        const spots = this.configuration.spots;
        const dataset = this.configuration.data;

        const spotRow = dataset?.find((x: any) => x.rowId.toString() === element.rowId);
        spotRow.spotid = 0;
        spotRow.imageid = 0;
        const index = spots?.findIndex((x: any) => x.spotText == element.rowId);
        if (index != undefined && index > -1) {
          spots?.splice(index, 1);
        }
        this.emitChangedConfiguration(this.configuration);
        if (!this.builder) {
          this.loadImages();
        }
      }
    });
  }

  isBuilderColumn(column: any): boolean {
    let exist = false;
    const colsBuilder = this.configuration?.columnsBuilder?.map(x => x.name);

    exist = !!colsBuilder?.includes(column);

    return exist;
  }

  rowSpan(column: any, item: any, row: any) {
    if (row % 6 == 0 || column.name == 'id' || column.name == 'spotid' || column.name == 'settings' || column.name == 'values') {
      if (column.name == 'settings' || column.name == 'values') {
        return 1;
      }
      else if (column.name == 'id' || this.isBuilderColumn(column.name)) {
        return item.idSpan;
      }
      else if (column.name == 'spotid' || !this.isBuilderColumn(column.name)) {
        return item.spotSpan;
      }
    }
    return 0;
  }

  checkRoles() {
    return this.hasRoles(this.roleIds);
  }

  setRow(e: any) {
    if (this.dataTable) {
      if (e) {
        this.row = e;
        this.action = Action.AddSpot;
        this.dataTable.addingSpot = true;
        this.dataTable.IdSpotAdd = e.rowId;
      }
      else {
        this.dataTable.IdSpotAdd = 0;
        this.dataTable.addingSpot = false;
        this.row = null;
        this.action = Action.None;
        this.ImageSpotId = 0;
        this.rowId = 0;
        this.showImageOptions = false;
      }
    }
  }

  override scrollToTop() {
    $('.card-footer').scrollTop(0);
  }

  @HostListener('window:resize')
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.innerWidth = window.innerWidth;
      this.loadImages();
    }, 200);
  }

  historySelected(e: any) {
    this.configuration = e.configuration;
    this.compareConfiguration = e.compare;
    this.loadCanvas();
  }

  historyRevertTo(log: TransactionLog) {
    const configuration = this.utils.JSONparse(log.objectJson) as RadTableConfiguration;
    this.emitChangedConfiguration(configuration);
  }

}
export class ListImage {
  id!: number;
  name!: string;
  image!: string;
  imageWidth!: number;
  imageHeight!: number;
  canBeDeleted?: boolean;
}

export enum Action {
  None = 0,
  AddSpot = 1,
  ImageOptions = 2
}

export class SpotsOrder {
  id!: number;
  rowId!: number;
  newRowId?: string;
}

export enum SpotOrigin {
  BUILDER = 0,
  CHECKLIST = 1
}

export class Spot {
  id!: number;
  posx!: number;
  posy!: number;
  spotText!: string;
  imageid!: number;
  percX!: number;
  percY!: number;
  origin!: SpotOrigin;
}

export interface RadTableConfiguration {
  channels?: boolean;
  colorize?: boolean;
  columnsBuilder?: TableColumn[];
  columnsChecklist?: TableColumn[];
  data?: any[];
  images?: ListImage[];
  spots?: Spot[];
  includeIM?: boolean;
  includeIMPos?: number;
}

export interface RADTableHistory {
  index: number;
  configuration: RadTableConfiguration;
  compare?: RadTableConfiguration;
}
